import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const HomepageCards = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCards = async () => {
      try {
        const response = await fetch(`${API_URL}/api/homepage-cards`);
        if (response.ok) {
          const data = await response.json();
          setCards(data);
        }
      } catch (error) {
        console.error('Error loading homepage cards:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadCards();
  }, []);

  if (loading || cards.length === 0) {
    return null;
  }

  // Determine the link for each card based on card_type
  const getCardLink = (card) => {
    if (card.card_type === 'packages') {
      // Package showcase cards link to the showcase page using slug or id
      const identifier = card.slug || card.id;
      return `/showcase/${identifier}`;
    }
    // Regular link cards use the configured link_url
    return card.link_url || '/';
  };

  return (
    <section className="py-16 bg-navy" data-testid="homepage-cards-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3">
            Descoperă mai mult
          </h2>
          <p className="text-white/60">
            Oferte speciale și destinații populare
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card) => (
            <Link
              key={card.id}
              to={getCardLink(card)}
              className="group relative h-64 rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(212,175,55,0.3)]"
              data-testid={`homepage-card-${card.id}`}
            >
              <img
                src={card.image_url}
                alt={card.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-transparent" />
              
              {/* Package count badge for showcase cards */}
              {card.card_type === 'packages' && card.packages && card.packages.length > 0 && (
                <div className="absolute top-3 right-3 bg-gold text-navy px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                  <Package className="w-3.5 h-3.5" />
                  {card.packages.length} {card.packages.length === 1 ? 'pachet' : 'pachete'}
                </div>
              )}
              
              <div className="absolute inset-0 flex flex-col justify-end p-6">
                <h3 className="font-heading font-bold text-xl text-white mb-2">
                  {card.title}
                </h3>
                {card.description && (
                  <p className="text-white/70 text-sm mb-4 line-clamp-2">
                    {card.description}
                  </p>
                )}
                <div className="flex items-center gap-1 text-gold text-sm font-medium group-hover:gap-2 transition-all">
                  <span>{card.card_type === 'packages' ? 'Vezi pachetele' : (card.link_text || 'Vezi mai mult')}</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
